
import { geometry } from "@turf/turf";
import apiService from "../common/utils/apiService";
import * as searchActions from "../slices/searchSlice";
import { getCentroidFromBbox } from "../common/utils/helperFunctions";
import { hideSidebarRight } from "../slices/mapSlice";
import { getCollectionStatsSearch } from "./index.service";

const STAC_URL = `/stac`;

// Filters box sets filteredCollections (multi-select); older single-collection
// flows (e.g. OrderCreator's FeaturesBar) only set filteredCollection. Fall back
// to that so both keep working without each needing to know about the other.
const getSelectedCollectionIDs = (filters) => {
  const collections = filters.filteredCollections?.length > 0
    ? filters.filteredCollections
    : (filters.filteredCollection?.collectionID ? [filters.filteredCollection] : []);
  return collections.map((collection) => collection.collectionID);
};

/* To communicate with STAC SERVER */

export const getStacData = (bbox, page) => async (dispatch, getState) => {
  dispatch(searchActions.getStacDataRequest());
  const state = getState();
  const filters = state.filters;
  const dateRange = `${filters.startDate}/${filters.endDate}`;
  const limit = filters.defaultExplorerLimit;
  const collection = filters.filteredCollection;
  const isBboxAvailable = `${bbox ? `&bbox=${bbox}` : ''}`;
  const isPageAvailable = `${page ? `&page=${page}` : ''}`;
  try {
    let url = `${STAC_URL}/collections/${collection}/items?datetime=${dateRange}${isBboxAvailable}${isPageAvailable}&limit=${limit}`;
    const response = await apiService.get(url, null, {}, true);
    dispatch(searchActions.setFilteredStacCollection(response));
  }
  catch (error) {
    dispatch(searchActions.getStacDataFailure(error));
    console.error('Error while fetching the stac data:', error);
  }
};

export const searchStacData = (page) => async (dispatch, getState) => {
  dispatch(searchActions.searchStacDataRequest());
  dispatch(hideSidebarRight());
  const state = getState();
  const map = state.map;
  const intersects = Object.keys(map.geometry).length > 0 ? map.geometry : null;
  const filters = state.filters;
  const itemsLimit = filters.defaultSearchLimit;
  const collectionIDs = getSelectedCollectionIDs(filters);
  const searchParams = {
    collections: collectionIDs,
    limit: itemsLimit,
    intersects,
    page,
  };
  if (filters.startDate && filters.endDate) {
    searchParams.datetime = `${filters.startDate}/${filters.endDate}`;
  }
  try {
    let url = `${STAC_URL}/search`;
    const response = await apiService.post(url, searchParams, {}, true);
    dispatch(searchActions.searchStacDataSuccess(response));
    // Stats stay scoped to the full applied range (monthRange), not the
    // narrower startDate/endDate a Timeline month/week click sets - otherwise
    // clicking a single month would wipe out every other month's count.
    const monthRange = filters.monthRange;
    const statsDatetimeGTE = monthRange.length > 0
      ? monthRange[0].monthStartEndDates.startDate
      : filters.startDate;
    const statsDatetimeLTE = monthRange.length > 0
      ? monthRange[monthRange.length - 1].monthStartEndDates.endDate
      : filters.endDate;
    dispatch(getCollectionStatsSearch({
      collectionIDs,
      aoi: intersects,
      datetimeGTE: statsDatetimeGTE,
      datetimeLTE: statsDatetimeLTE,
    }));
  }
  catch (error) {
    dispatch(searchActions.searchStacDataFailure(error));
    console.error('Error while searching the stac data:', error);
  }
};

// index-server rejects an `ids` filter on /stac/search (both GET and POST
// handlers hard-error "ids filter not supported" - ItemFilter never grew an
// Ids column). Fetch one item at a time via the OGC API Features single-item
// endpoint instead; caller loops over an order's item ids and dispatches this
// once per id, same as searchMultiStacData is dispatched once per AOI.
// Returns the fetched feature so a caller can await one specific dispatch
// (e.g. to select the first item) without changing this fire-and-forget-per-id shape.
export const searchOrderItem = (collectionID, itemID) => async (dispatch) => {
  dispatch(searchActions.searchStacDataRequest());
  try {
    let url = `${STAC_URL}/collections/${collectionID}/items/${itemID}`;
    const response = await apiService.get(url, null, {}, true);
    dispatch(searchActions.searchOrderItemSuccess(response));
    return response;
  }
  catch (error) {
    dispatch(searchActions.searchStacDataFailure(error));
    console.error('Error while fetching order item:', error);
  }
};

export const searchObjectDetectionData = (collectionID) => async (dispatch, getState) => {
  dispatch(searchActions.searchObjectDetectionDataRequest());
  const state = getState();
  const map = state.map;
  const intersects = Object.keys(map.geometry).length > 0 ? map.geometry : null;
  const filters = state.filters;
  const itemsLimit = 10000;
  const collection = collectionID;
  const searchParams = {
    collections: [collection],
    limit: itemsLimit,
    intersects,
  };
  if (filters.startDate && filters.endDate) {
    searchParams.datetime = `${filters.startDate}/${filters.endDate}`;
  }
  try {
    let url = `${STAC_URL}/search`;
    const response = await apiService.post(url, searchParams, {}, true);
    dispatch(searchActions.searchObjectDetectionDataSuccess(response));
  }
  catch (error) {
    dispatch(searchActions.searchObjectDetectionDataFailure(error));
    console.error('Error while searching the detected object:', error);
  }
};

export const searchMultiStacData = (geometry,name) => async (dispatch, getState) => {
  dispatch(searchActions.getMultiStacDataRequest());
  const state = getState();
  const intersects = geometry;
  const filters = state.filters;
  const itemsLimit = filters.defaultSearchLimit;
  const searchParams = {
    collections: getSelectedCollectionIDs(filters),
    limit: itemsLimit,
    intersects,
  };
  if (filters.startDate && filters.endDate) {
    searchParams.datetime = `${filters.startDate}/${filters.endDate}`;
  }
  try {
    let url = `${STAC_URL}/search`;
    const response = await apiService.post(url, searchParams, {}, true);
    const matched = response?.numberMatched;

    const resultSummary = {
      name,
      geometry,
      matched,
    };
    dispatch(searchActions.getMultiStacDataSuccess(resultSummary));
    dispatch(searchActions.getMultiStacHighlightData(response));
  }
  catch (error) {
    dispatch(searchActions.getMultiStacDataFailure(error));
    console.error('Error while searching the stac data:', error);
  }
};

export const getStacCollections = (callback) => async (dispatch, getState) => {
  dispatch(searchActions.getStacCollectionsRequest());
  const state = getState();
  try {
    let url = `${STAC_URL}/collections`;
    const response = await apiService.get(url, null, {}, true);
    dispatch(searchActions.getStacCollectionsSuccess(response));
    if (callback && typeof callback === 'function') {
      callback(response.collections, state.collectionDetails.collections);
    }
  }
  catch (error) {
    dispatch(searchActions.getStacCollectionsFailure(error));
    console.error('Error while getting the stac collections', error);
  }
};


// Searches the collections selected in the filters; falls back to the clicked item's own collection
export const searchSimilarStacData = (bbox, collectionID) => async (dispatch, getState) => {
  dispatch(searchActions.searchSimilarStacDataRequest());
  const state = getState();
  const intersects = getCentroidFromBbox(bbox);
  const selectedCollections = getSelectedCollectionIDs(state.filters);
  const collections = selectedCollections.length > 0
    ? selectedCollections
    : (collectionID ? [collectionID] : []);
  const searchParams = {
    collections,
    limit: 40,
    intersects,
  };
  try {
    let url = `${STAC_URL}/search`;
    const response = await apiService.post(url, searchParams, {}, true);
    dispatch(searchActions.searchSimilarStacDataSuccess(response));
  }
  catch (error) {
    dispatch(searchActions.searchSimilarStacDataFailure(error));
    console.error('Error while searching the stac data:', error);
  }
};